import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import PageTransition from "components/PageTransition";
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import { useAuth } from './contexts/AuthContext';

// Eagerly loaded — tiny, always needed
import NotFound from "pages/NotFound";

// Lazy-loaded pages — each becomes its own JS chunk
const Login = lazy(() => import('./pages/login'));
const HomePage = lazy(() => import('./pages/home-page'));
const Registration = lazy(() => import('./pages/registration'));
const Dashboard = lazy(() => import('./pages/dashboard'));
const Listings = lazy(() => import('./pages/listings'));
const Messages = lazy(() => import('./pages/messages'));
const ProfileSettings = lazy(() => import('./pages/profile-settings'));
const TransactionHistory = lazy(() => import('./pages/transaction-history'));
const NotificationsPage = lazy(() => import('./pages/notifications'));
const CommunityOpportunities = lazy(() => import('./pages/community-opportunities'));
const ItemDetail = lazy(() => import('./pages/item-detail'));
const ReviewSubmission = lazy(() => import('./pages/review-submission'));
const CreateListing = lazy(() => import('./pages/create-listing'));
const ListingAnalytics = lazy(() => import('./pages/listing-analytics'));
const ReviewManagement = lazy(() => import('./pages/review-management'));
const Watchlist = lazy(() => import('./pages/watchlist'));
const SearchPage = lazy(() => import('./pages/search'));
const HowToUsePlatform = lazy(() => import('./pages/how-to-use-platform'));
const AuthConfirm = lazy(() => import('./pages/auth-confirm'));
const MatchNotifications = lazy(() => import('./pages/match-notifications'));
const NegotiationTemplates = lazy(() => import('./pages/negotiation-templates'));
const VideoChatInterface = lazy(() => import('./pages/video-chat-interface'));
const VideoCallScheduling = lazy(() => import('./pages/video-call-scheduling'));
const BusinessRegistration = lazy(() => import('./pages/business-registration'));
const BusinessStorefront = lazy(() => import('./pages/business-storefront'));
const BusinessDirectory = lazy(() => import('./pages/business-directory'));
const TradingGroupsDirectory = lazy(() => import('./pages/trading-groups-directory'));
const TradingGroupDetail = lazy(() => import('./pages/trading-group-detail'));
const CreateTradingGroup = lazy(() => import('./pages/create-trading-group'));
const RealTimeActivityFeed = lazy(() => import('./pages/real-time-activity-feed'));
const TradingGroupAdminDashboard = lazy(() => import('./pages/trading-group-admin-dashboard'));
const AdminTrialSetup = lazy(() => import('./pages/admin-trial-setup'));
const AdminModerationPanel = lazy(() => import('./pages/admin-moderation'));
const AdminDeploymentStatus = lazy(() => import('./pages/admin-deployment-status'));
const AdminSetupChecklist = lazy(() => import('./pages/admin-setup-checklist'));
const AdminAnalytics = lazy(() => import('./pages/admin-analytics'));
const AdminDatabaseHealth = lazy(() => import('./pages/admin-database-health'));
const ContactPage = lazy(() => import('./pages/contact'));
const AboutPage = lazy(() => import('./pages/about'));
const PrivacyPolicyPage = lazy(() => import('./pages/privacy-policy'));
const TermsOfServicePage = lazy(() => import('./pages/terms'));
const SubscriptionBilling = lazy(() => import('./pages/subscription-billing'));
const BrandPage = lazy(() => import('./pages/brand'));

// Minimal fallback shown while a lazy chunk loads
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Route guard: requires authentication + admin or moderator role
function AdminRoute({ children }) {
  const { user, userProfile, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  const role = userProfile?.role;
  if (role !== 'admin' && role !== 'moderator') return <Navigate to="/dashboard" replace />;
  return children;
}

function AnimatedRoutes() {
  const location = useLocation();
  useGoogleAnalytics();

  return (
    <Suspense fallback={<PageLoader />}>
      <AnimatePresence mode="wait" initial={false}>
        <RouterRoutes location={location} key={location?.pathname}>
          <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
          <Route path="/login" element={<PageTransition><Login /></PageTransition>} />
          <Route path="/register" element={<PageTransition><Registration /></PageTransition>} />
          <Route path="/auth/confirm" element={<PageTransition><AuthConfirm /></PageTransition>} />
          <Route path="/home-page" element={<PageTransition><HomePage /></PageTransition>} />
          <Route path="/dashboard" element={<PageTransition><Dashboard /></PageTransition>} />
          <Route path="/explore" element={<PageTransition><Listings /></PageTransition>} />
          <Route path="/messages" element={<PageTransition><Messages /></PageTransition>} />
          <Route path="/profile-settings" element={<PageTransition><ProfileSettings /></PageTransition>} />
          <Route path="/transaction-history" element={<PageTransition><TransactionHistory /></PageTransition>} />
          <Route path="/trade-history" element={<Navigate to="/transaction-history" replace />} />
          <Route path="/item-detail" element={<PageTransition><ItemDetail /></PageTransition>} />
          <Route path="/item-detail/:id" element={<PageTransition><ItemDetail /></PageTransition>} />
          <Route path="/notifications" element={<PageTransition><NotificationsPage /></PageTransition>} />
          <Route path="/community-opportunities" element={<PageTransition><CommunityOpportunities /></PageTransition>} />
          <Route path="/review-submission" element={<PageTransition><ReviewSubmission /></PageTransition>} />
          <Route path="/create-listing" element={<PageTransition><CreateListing /></PageTransition>} />
          <Route path="/listing-analytics" element={<PageTransition><ListingAnalytics /></PageTransition>} />
          <Route path="/review-management" element={<PageTransition><ReviewManagement /></PageTransition>} />
          <Route path="/watchlist" element={<PageTransition><Watchlist /></PageTransition>} />
          <Route path="/search" element={<PageTransition><SearchPage /></PageTransition>} />
          <Route path="/how-to-use-platform" element={<PageTransition><HowToUsePlatform /></PageTransition>} />
          <Route path="/wishlist-management" element={<Navigate to="/watchlist" replace />} />
          <Route path="/match-notifications" element={<PageTransition><MatchNotifications /></PageTransition>} />
          <Route path="/negotiation-templates" element={<PageTransition><NegotiationTemplates /></PageTransition>} />
          <Route path="/video-chat-interface" element={<PageTransition><VideoChatInterface /></PageTransition>} />
          <Route path="/video-call-scheduling" element={<PageTransition><VideoCallScheduling /></PageTransition>} />
          <Route path="/business-registration" element={<PageTransition><BusinessRegistration /></PageTransition>} />
          <Route path="/business-directory" element={<PageTransition><BusinessDirectory /></PageTransition>} />
          <Route path="/business-storefront/:slug" element={<PageTransition><BusinessStorefront /></PageTransition>} />
          <Route path="/trading-groups-directory" element={<PageTransition><TradingGroupsDirectory /></PageTransition>} />
          <Route path="/trading-group-detail" element={<PageTransition><TradingGroupDetail /></PageTransition>} />
          <Route path="/create-trading-group" element={<PageTransition><CreateTradingGroup /></PageTransition>} />
          <Route path="/real-time-activity-feed" element={<PageTransition><RealTimeActivityFeed /></PageTransition>} />
          <Route path="/trading-group-admin-dashboard" element={<PageTransition><TradingGroupAdminDashboard /></PageTransition>} />
          <Route path="/admin/trial-setup" element={<AdminRoute><PageTransition><AdminTrialSetup /></PageTransition></AdminRoute>} />
          <Route path="/admin/moderation" element={<AdminRoute><PageTransition><AdminModerationPanel /></PageTransition></AdminRoute>} />
          <Route path="/admin/deployment-status" element={<AdminRoute><PageTransition><AdminDeploymentStatus /></PageTransition></AdminRoute>} />
          <Route path="/admin/setup-checklist" element={<AdminRoute><PageTransition><AdminSetupChecklist /></PageTransition></AdminRoute>} />
          <Route path="/admin/analytics" element={<AdminRoute><PageTransition><AdminAnalytics /></PageTransition></AdminRoute>} />
          <Route path="/admin/database-health" element={<AdminRoute><PageTransition><AdminDatabaseHealth /></PageTransition></AdminRoute>} />
          <Route path="/contact" element={<PageTransition><ContactPage /></PageTransition>} />
          <Route path="/about" element={<PageTransition><AboutPage /></PageTransition>} />
          <Route path="/privacy" element={<PageTransition><PrivacyPolicyPage /></PageTransition>} />
          <Route path="/terms" element={<PageTransition><TermsOfServicePage /></PageTransition>} />
          <Route path="/subscription-billing" element={<PageTransition><SubscriptionBilling /></PageTransition>} />
          <Route path="/brand" element={<PageTransition><BrandPage /></PageTransition>} />
          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </RouterRoutes>
      </AnimatePresence>
    </Suspense>
  );
}

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <AnimatedRoutes />
      </ErrorBoundary>
    </BrowserRouter>
  );
}